import { useState, useEffect } from 'react';
import { Layers, Loader2, ExternalLink, Package, AlertTriangle, Image as ImageIcon, Boxes } from 'lucide-react';
import { fmtCena } from '../receptura/receptUtils';

const KANALY = [
    { id: 'bistro', label: 'Bistro', color: '#94A3B8' },
    { id: 'rozvoz', label: 'Rozvoz', color: '#10B981' },
    { id: 'vydej',  label: 'Výdej',  color: '#06B6D4' },
];

// Paleta pro rozpad balení na jednotlivé obaly v donutu
const PALETA = ['#10B981', '#06B6D4', '#F59E0B', '#8B5CF6', '#EC4899', '#EAB308', '#F97316'];

/**
 * Stálá karta vpravo u balicího předpisu — protějšek kalkulační karty
 * receptury, včetně jejího rytmu: donut, rozpad 2×2, info dlaždice.
 *
 * Donut kreslí rozpad nejdražšího kanálu (co v tom balení kolik stojí) —
 * to je skutečný celek. Kanály samy o sobě celek netvoří, jsou to varianty,
 * takže ty jdou do dlaždic vedle sebe, ne do koláče.
 */
export default function PredpisStatistika({
    predpis, isDark, mutedColor, secondaryColor, cardStyle, akcent = '#10B981', onNavigate,
}) {
    const [polozky, setPolozky] = useState(null);
    const [nacitam, setNacitam] = useState(false);

    const trackColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const tileBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
    const tileBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    useEffect(() => {
        if (!predpis?.id || !predpis.recepty_count) { setPolozky(null); return; }
        let zije = true;
        setNacitam(true);
        fetch('/api/recepty/recepty?typ=PRO', { headers: { Accept: 'application/json' } })
            .then(r => r.ok ? r.json() : [])
            .then(d => { if (zije) setPolozky((d || []).filter(x => x.balici_predpis_id === predpis.id)); })
            .catch(() => {})
            .finally(() => { if (zije) setNacitam(false); });
        return () => { zije = false; };
    }, [predpis?.id, predpis?.recepty_count]);

    if (!predpis) return null;

    const kanaly = KANALY.map(k => ({
        ...k,
        naklad: predpis.kanaly[k.id]?.naklad || 0,
        polozky: predpis.kanaly[k.id]?.polozky || [],
        media: predpis.kanaly[k.id]?.media || [],
    }));

    const nejdrazsi = kanaly.reduce((a, b) => (b.naklad > a.naklad ? b : a), kanaly[0]);
    const total = nejdrazsi.naklad;
    const rozsah = Math.max(...kanaly.map(k => k.naklad)) - Math.min(...kanaly.map(k => k.naklad));
    const maxKanal = Math.max(...kanaly.map(k => k.naklad), 0.0001);

    // Donut — rozpad nejdražšího kanálu po jednotlivých obalech
    const size = 190, sw = 26, R = (size - sw) / 2, C = 2 * Math.PI * R, cx = size / 2;
    let acc = 0;
    const segs = nejdrazsi.polozky
        .map((r, i) => ({ ...r, color: PALETA[i % PALETA.length] }))
        .filter(r => r.naklad > 0)
        .map(r => {
            const len = (r.naklad / Math.max(total, 0.0001)) * C;
            const seg = { ...r, len, offset: acc };
            acc += len;
            return seg;
        });

    const fotekCelkem = kanaly.reduce((s, k) => s + k.media.length, 0);
    const polozekCelkem = kanaly.reduce((s, k) => s + k.polozky.length, 0);
    const kanaluSBalenim = kanaly.filter(k => k.polozky.length > 0).length;
    const bezCeny = kanaly.some(k => k.polozky.some(r => r.cena_chybi));

    const infoTile = (Icon, value, label, zvyraznit = false) => (
        <div className="rounded-lg py-2.5 flex flex-col items-center gap-1" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
            <Icon size={15} style={{ color: zvyraznit ? akcent : mutedColor }} />
            <div className="text-sm font-bold leading-none" style={{ color: 'var(--text-primary)' }}>{value}</div>
            <div className="text-[9px] text-center" style={{ color: mutedColor }}>{label}</div>
        </div>
    );

    return (
        <div className="rounded-xl flex flex-col" style={{ ...cardStyle, flexShrink: 0 }}>
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: secondaryColor }}>Balení</span>
                {nacitam
                    ? <Loader2 size={12} className="animate-spin" style={{ color: mutedColor }} />
                    : <span className="text-[10px]" style={{ color: mutedColor }}>{nejdrazsi.label.toLowerCase()} = nejdražší</span>}
            </div>

            <div className="p-4">
                {/* Donut — z čeho se skládá nejdražší balení */}
                <div className="relative" style={{ width: size, height: size, margin: '0 auto' }}>
                    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx={cx} cy={cx} r={R} fill="none" stroke={trackColor} strokeWidth={sw} />
                        {segs.map(s => (
                            <circle key={s.id} cx={cx} cy={cx} r={R} fill="none" stroke={s.color} strokeWidth={sw} strokeLinecap="butt"
                                strokeDasharray={`${s.len} ${C - s.len}`} strokeDashoffset={-s.offset}
                                style={{ transition: 'stroke-dasharray 0.4s, stroke-dashoffset 0.4s' }} />
                        ))}
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="text-[28px] font-bold leading-none" style={{ color: 'var(--text-primary)' }}>{total.toFixed(2)}</div>
                        <div className="text-[11px] mt-1" style={{ color: mutedColor }}>Kč / balení</div>
                        {rozsah > 0.005 && (
                            <div className="mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                style={{ background: 'rgba(16,185,129,0.12)', color: akcent }}>
                                rozdíl {rozsah.toFixed(2)} Kč
                            </div>
                        )}
                    </div>
                </div>

                {/* Z čeho se skládá — legenda donutu */}
                {segs.length > 0 && (
                    <div className="mt-3 space-y-1">
                        {segs.map(s => (
                            <div key={s.id} className="flex items-center gap-2">
                                <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                                <span className="text-[11px] flex-1 truncate" style={{ color: secondaryColor }}>{s.nazev}</span>
                                <span className="text-[10px]" style={{ color: s.color, width: 30, textAlign: 'right' }}>
                                    {total > 0 ? ((s.naklad / total) * 100).toFixed(0) : 0}%
                                </span>
                                <span className="text-[11px] font-semibold" style={{ color: 'var(--text-primary)', width: 58, textAlign: 'right' }}>
                                    {fmtCena(s.naklad)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Kanály — varianty, ne části celku, proto dlaždice vedle sebe */}
                <div className="grid grid-cols-3 gap-2 mt-4">
                    {kanaly.map(k => (
                        <div key={k.id} className="rounded-lg p-2.5" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
                            <div className="flex items-center gap-1.5 mb-1">
                                <span style={{ width: 7, height: 7, borderRadius: 2, background: k.color, display: 'inline-block' }} />
                                <span className="text-[10px]" style={{ color: secondaryColor }}>{k.label}</span>
                            </div>
                            <div className="text-sm font-bold leading-none" style={{ color: k.naklad > 0 ? 'var(--text-primary)' : mutedColor }}>
                                {k.naklad.toFixed(2)}
                            </div>
                            <div className="mt-1.5 rounded-full overflow-hidden" style={{ height: 4, background: trackColor }}>
                                <div style={{ width: `${Math.min(100, (k.naklad / maxKanal) * 100)}%`, height: '100%', background: k.color, borderRadius: 99, transition: 'width 0.3s' }} />
                            </div>
                            <div className="text-[9px] mt-1" style={{ color: mutedColor }}>
                                {k.polozky.length === 0 ? 'nebalí se' : `${k.polozky.length} pol.`}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Info dlaždice */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                    {infoTile(Boxes, polozekCelkem, 'položek celkem')}
                    {infoTile(Layers, kanaluSBalenim + '/3', 'kanálů balí')}
                    {infoTile(ImageIcon, fotekCelkem, fotekCelkem === 1 ? 'fotka' : 'fotek', fotekCelkem > 0)}
                    {infoTile(Package, predpis.recepty_count, 'položek nese', predpis.recepty_count > 0)}
                </div>

                {/* Kdo předpis nese */}
                {predpis.recepty_count > 0 && (
                    <div className="mt-3 rounded-lg p-3" style={{ background: tileBg, border: `1px solid ${tileBorder}` }}>
                        <div className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: mutedColor }}>
                            Úprava se propíše
                        </div>
                        {(polozky || []).slice(0, 5).map(p => (
                            <button key={p.id}
                                onClick={() => onNavigate?.('produkt-kvalita', { polozka: { typ: p.typ, id: p.id } })}
                                className="flex items-center gap-1.5 w-full text-left py-0.5"
                                style={{ background: 'none', border: 'none', cursor: onNavigate ? 'pointer' : 'default', padding: 0 }}>
                                <span className="text-[11px] truncate flex-1" style={{ color: secondaryColor }}>{p.nazev}</span>
                                {onNavigate && <ExternalLink size={9} style={{ color: mutedColor, flexShrink: 0 }} />}
                            </button>
                        ))}
                        {polozky && polozky.length > 5 && (
                            <div className="text-[10px] mt-1" style={{ color: mutedColor }}>… a dalších {polozky.length - 5}</div>
                        )}
                    </div>
                )}

                {/* Co chybí */}
                {(polozekCelkem === 0 || bezCeny || fotekCelkem === 0) && (
                    <div className="mt-3 rounded-lg p-3 space-y-1.5" style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.22)' }}>
                        {polozekCelkem === 0 && (
                            <div className="flex items-start gap-1.5 text-[11px]" style={{ color: '#F97316' }}>
                                <AlertTriangle size={11} style={{ flexShrink: 0, marginTop: 1 }} /> Předpis nemá jedinou položku
                            </div>
                        )}
                        {bezCeny && (
                            <div className="flex items-start gap-1.5 text-[11px]" style={{ color: '#EF4444' }}>
                                <AlertTriangle size={11} style={{ flexShrink: 0, marginTop: 1 }} /> Některý obal nemá platnou cenu
                            </div>
                        )}
                        {fotekCelkem === 0 && (
                            <div className="flex items-start gap-1.5 text-[11px]" style={{ color: mutedColor }}>
                                <AlertTriangle size={11} style={{ flexShrink: 0, marginTop: 1 }} /> Bez fotek — obsluha nemá podle čeho balit
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
